import { I18nProvider } from 'react-aria-components';
import { RouterProvider } from 'react-router';

import { ApiError } from '@/api/client.ts';
import router from '@/app/router.tsx';
import ApplicationContextProvider from '@/contexts/ApplicationContext.tsx';
import { recordError } from '@/core/errorLog.ts';
import { LOCALES, useLanguage } from '@/core/i18n/index.ts';
import useDarkMode from '@/utils/useDarkMode.tsx';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';

const queryClient = new QueryClient({
    // Every failed request, also one no page shows, goes into the error log a reported problem sends along.
    queryCache: new QueryCache({ onError: recordError }),
    mutationCache: new MutationCache({ onError: recordError }),
    defaultOptions: {
        queries: {
            // Mutations invalidate what they change, so other data can be reused across pages instead of refetching
            // every list on each navigation.
            staleTime: 30_000,
            // Client errors (404, 422) won't fix themselves, so only retry network and server errors.
            retry: (failureCount, error) => failureCount < 2 && !(error instanceof ApiError && error.status < 500),
        },
    },
});

const App = () => {
    const theme = useDarkMode();
    const language = useLanguage();

    return (
        <ApplicationContextProvider {...theme}>
            <QueryClientProvider client={queryClient}>
                <I18nProvider locale={LOCALES[language]}>
                    <RouterProvider router={router} />
                </I18nProvider>
            </QueryClientProvider>
        </ApplicationContextProvider>
    );
};

export default App;
